import { doc, updateDoc, increment, setDoc, collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { PaymentItem } from '../types';

// Commission summary interface
export interface CommissionSummary {
  totalEarnings: number;
  pendingCommissions: number;
  approvedCommissions: number;
  totalReferrals: number;
}

// Commission rates for different product types
export const COMMISSION_RATES = {
  course: 0.15, // 15% for course enrollments
  book: 0.10,   // 10% for book purchases
  video: 0.10,  // 10% for video purchases
  subscription: 0.20, // 20% for premium subscriptions
  default: 0.10 // 10% default rate
};

// Commission transaction types
export interface CommissionTransaction {
  id: string;
  referrerId: string; // User ID of the person who gets the commission
  referredUserId: string; // User ID of the person who made the purchase
  orderId: string;
  paymentId: string;
  items: PaymentItem[];
  totalAmount: number;
  commissionAmount: number;
  commissionRate: number;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  createdAt: Date;
  updatedAt: Date;
  paidAt?: Date;
  notes?: string;
}

// Calculate commission for a purchase
export const calculateReferralCommission = (
  items: PaymentItem[],
  referredBy: string
): { commissionAmount: number; commissionDetails: any[] } | null => {
  if (!referredBy) return null;

  let totalCommission = 0;
  const commissionDetails: any[] = [];

  items.forEach(item => {
    const rate = COMMISSION_RATES[item.type as keyof typeof COMMISSION_RATES] || COMMISSION_RATES.default;
    const itemCommission = item.amount * rate;
    
    totalCommission += itemCommission;
    
    commissionDetails.push({
      itemId: item.id,
      itemName: item.name,
      itemType: item.type,
      itemAmount: item.amount,
      commissionRate: rate,
      commissionAmount: itemCommission
    });
  });

  return {
    commissionAmount: totalCommission,
    commissionDetails
  };
};

// Create commission transaction record
export const createCommissionTransaction = async (
  referrerId: string,
  referredUserId: string,
  orderId: string,
  paymentId: string,
  items: PaymentItem[],
  totalAmount: number,
  commissionAmount: number,
  commissionDetails: any[]
): Promise<string> => {
  try {
    const commissionId = `COMM_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const commissionTransaction: CommissionTransaction = {
      id: commissionId,
      referrerId,
      referredUserId,
      orderId,
      paymentId,
      items,
      totalAmount,
      commissionAmount,
      commissionRate: commissionAmount / totalAmount,
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date(),
      notes: `Commission for order ${orderId} - ${items.length} item(s)`
    };

    // Save commission transaction to Firestore
    const commissionRef = doc(db, 'commission_transactions', commissionId);
    await setDoc(commissionRef, commissionTransaction);

    // Update referrer's earnings
    const referrerRef = doc(db, 'users', referrerId);
    await updateDoc(referrerRef, {
      earnings: increment(commissionAmount),
      updatedAt: new Date()
    });

    // totalrefs is not incremented here - it's only incremented during signup
    // The totalrefs field tracks how many users were referred, not how many payments were made
    // await updateDoc(referrerRef, {
    //   totalrefs: increment(1),
    //   updatedAt: new Date()
    // });

    return commissionId;
  } catch (error) {
    console.error('Error creating commission transaction:', error);
    throw new Error('Failed to create commission transaction');
  }
};

// Process referral commission for a completed payment
export const processReferralCommission = async (
  referredBy: string,
  orderId: string,
  paymentId: string,
  items: PaymentItem[],
  totalAmount: number
): Promise<string | null> => {
  if (!referredBy) return null;

  try {
    // Calculate commission
    const commissionResult = calculateReferralCommission(items, referredBy);
    if (!commissionResult) return null;

    const { commissionAmount, commissionDetails } = commissionResult;

    // Create commission transaction
    const commissionId = await createCommissionTransaction(
      referredBy,
      '', // This will be filled when we have the user ID
      orderId,
      paymentId,
      items,
      totalAmount,
      commissionAmount,
      commissionDetails
    );

    console.log(`Commission processed: ${commissionAmount} GHS for referrer ${referredBy}`);
    return commissionId;
  } catch (error) {
    console.error('Error processing referral commission:', error);
    throw new Error('Failed to process referral commission');
  }
};

// Get commission transactions for a user
export const getUserCommissionTransactions = async (userId: string): Promise<CommissionTransaction[]> => {
  try {
    const commissionRef = collection(db, 'commission_transactions');
    const q = query(commissionRef, where('referrerId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const transactions: CommissionTransaction[] = [];
    querySnapshot.forEach((doc) => {
      transactions.push(doc.data() as CommissionTransaction);
    });

    return transactions.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  } catch (error) {
    console.error('Error fetching commission transactions:', error);
    return [];
  }
};

// Get commission summary for a user
export const getUserCommissionSummary = async (userId: string): Promise<{
  totalEarnings: number;
  pendingCommissions: number;
  approvedCommissions: number;
  totalReferrals: number;
}> => {
  try {
    const transactions = await getUserCommissionTransactions(userId);
    
    const totalEarnings = transactions
      .filter(t => t.status === 'approved' || t.status === 'paid')
      .reduce((sum, t) => sum + t.commissionAmount, 0);
    
    const pendingCommissions = transactions
      .filter(t => t.status === 'pending')
      .reduce((sum, t) => sum + t.commissionAmount, 0);
    
    const approvedCommissions = transactions
      .filter(t => t.status === 'approved')
      .reduce((sum, t) => sum + t.commissionAmount, 0);
    
    const totalReferrals = transactions.length;

    return {
      totalEarnings,
      pendingCommissions,
      approvedCommissions,
      totalReferrals
    };
  } catch (error) {
    console.error('Error fetching commission summary:', error);
    return {
      totalEarnings: 0,
      pendingCommissions: 0,
      approvedCommissions: 0,
      totalReferrals: 0
    };
  }
};

// Approve a commission transaction (admin function)
export const approveCommissionTransaction = async (commissionId: string): Promise<void> => {
  try {
    const commissionRef = doc(db, 'commission_transactions', commissionId);
    await updateDoc(commissionRef, {
      status: 'approved',
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error approving commission transaction:', error);
    throw new Error('Failed to approve commission transaction');
  }
};

// Mark commission as paid
export const markCommissionAsPaid = async (commissionId: string): Promise<void> => {
  try {
    const commissionRef = doc(db, 'commission_transactions', commissionId);
    await updateDoc(commissionRef, {
      status: 'paid',
      paidAt: new Date(),
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error marking commission as paid:', error);
    throw new Error('Failed to mark commission as paid');
  }
};
